import { ExternalLink, Play } from "lucide-react";

interface VideoLinkProps {
  url: string;
  title: string;
  className?: string;
}

// Helper function to detect if URL is a video platform
const isVideoUrl = (url: string): boolean => {
  return (
    url.includes("youtube.com") ||
    url.includes("youtu.be") ||
    url.includes("vimeo.com") ||
    url.includes("instagram.com/reel") ||
    url.includes("instagram.com/p") ||
    url.includes("tiktok.com")
  );
};

// Helper function to extract YouTube video ID
const getYouTubeVideoId = (url: string): string | null => {
  if (url.includes("youtu.be/")) {
    return url.split("youtu.be/")[1].split("?")[0];
  } else if (url.includes("youtube.com/watch?v=")) {
    return url.split("v=")[1].split("&")[0];
  } else if (url.includes("youtube.com/embed/")) {
    return url.split("embed/")[1].split("?")[0];
  }
  return null;
};

// Helper function to extract Vimeo video ID
const getVimeoVideoId = (url: string): string | null => {
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  return vimeoMatch ? vimeoMatch[1] : null;
};

// Helper function to get TikTok embed URL
const getTikTokEmbedUrl = (url: string): string | null => {
  // TikTok URLs come in various formats, try to extract video ID
  const patterns = [
    /tiktok\.com\/@[^\/]+\/video\/(\d+)/,
    /tiktok\.com\/v\/(\d+)/,
    /tiktok\.com\/.*\/(\d+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://www.tiktok.com/embed/v2/${match[1]}`;
    }
  }
  return null;
};

// Helper function to get Instagram embed URL
const getInstagramEmbedUrl = (url: string): string | null => {
  // Instagram URLs: instagram.com/p/POST_ID or instagram.com/reel/REEL_ID
  const patterns = [
    /instagram\.com\/p\/([A-Za-z0-9_-]+)/,
    /instagram\.com\/reel\/([A-Za-z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://www.instagram.com/p/${match[1]}/embed/`;
    }
  }
  return null;
};

export default function VideoLink({
  url,
  title,
  className = "",
}: VideoLinkProps) {
  const isVideo = isVideoUrl(url);

  // If it's not a video URL, render as regular link
  if (!isVideo) {
    return (
      <div
        className={`p-3 border border-border rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors ${className}`}
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-primary/60 hover:text-primary transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          <span className="font-medium">{title}</span>
        </a>
      </div>
    );
  }

  // Handle YouTube videos
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      return (
        <div
          className={`space-y-3 p-4 border border-border rounded-lg bg-muted/20 ${className}`}
        >
          <h5 className="text-sm font-medium text-primary">{title}</h5>
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full max-w-md">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={title}
            />
          </div>
        </div>
      );
    }
  }

  // Handle Vimeo videos
  if (url.includes("vimeo.com")) {
    const videoId = getVimeoVideoId(url);
    if (videoId) {
      return (
        <div
          className={`space-y-3 p-4 border border-border rounded-lg bg-muted/20 ${className}`}
        >
          <h5 className="text-sm font-medium text-primary">{title}</h5>
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full max-w-md">
            <iframe
              src={`https://player.vimeo.com/video/${videoId}`}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={title}
            />
          </div>
        </div>
      );
    }
  }

  // Handle TikTok videos
  if (url.includes("tiktok.com")) {
    const embedUrl = getTikTokEmbedUrl(url);
    if (embedUrl) {
      return (
        <div
          className={`space-y-3 p-4 border border-border rounded-lg bg-muted/20 ${className}`}
        >
          <h5 className="text-sm font-medium text-primary">{title}</h5>
          <div
            className="relative rounded-lg overflow-hidden bg-black w-full max-w-xs mx-auto"
            style={{ aspectRatio: "9/16" }}
          >
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={title}
            />
          </div>
        </div>
      );
    }
  }

  // Handle Instagram videos/reels
  if (url.includes("instagram.com")) {
    const embedUrl = getInstagramEmbedUrl(url);
    if (embedUrl) {
      return (
        <div
          className={`space-y-3 p-4 border border-border rounded-lg bg-muted/20 ${className}`}
        >
          <h5 className="text-sm font-medium text-primary">{title}</h5>
          <div
            className="relative rounded-lg overflow-hidden bg-black w-full max-w-xs mx-auto"
            style={{ aspectRatio: "9/16" }}
          >
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={title}
            />
          </div>
        </div>
      );
    }
  }

  // Fallback: if video URL detection failed, show as regular link with video icon
  return (
    <div
      className={`p-3 border border-border rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors ${className}`}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm text-primary/60 hover:text-primary transition-colors"
      >
        <Play className="h-4 w-4" />
        <span className="font-medium">{title}</span>
      </a>
    </div>
  );
}
